import { motion } from 'motion/react'
import type { ReferenceStation } from '../../api/types'
import { cleanDeviceName } from '../../lib/utils'
import { useUnits } from '../../lib/UnitsProvider'
import { AsyncPanel, PanelSkeleton, Shimmer } from '../ui/AsyncPanel'
import { TOKENS } from '../ui/dashboard-primitives'
import { useReferencePanel } from './panelStates'

const COLS = 'minmax(0,1fr) 4.25rem 4.25rem 4.5rem'

/**
 * The Strength pillar unpacked: best logged load on each station vs the
 * age-matched reference. A table, not bars — the body map beside it is the
 * visualisation (decision D4). Through `AsyncPanel` (Spec 2 D5, #170), so a
 * failed request no longer draws as a table with only its header.
 */
export function ReferenceTable() {
  const reference = useReferencePanel()

  return (
    <AsyncPanel
      sources={reference.sources}
      hasContent={reference.hasContent}
      subject="Your strength reference"
      skeleton={
        <PanelSkeleton label="Loading strength reference">
          <div className="grid gap-3 mt-1.5">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Shimmer key={i} height={28} />
            ))}
          </div>
        </PanelSkeleton>
      }
    >
      {() => <StationRows stations={reference.data?.stations ?? []} />}
    </AsyncPanel>
  )
}

function StationRows({ stations }: { stations: ReferenceStation[] }) {
  const { formatLoad } = useUnits()

  return (
    <div className="grid mt-1.5">
      <div
        className="grid items-baseline gap-x-3.5 pb-1 text-[0.5625rem] tracking-[0.24em] uppercase"
        style={{ gridTemplateColumns: COLS, fontFamily: 'var(--font-mono)', color: TOKENS.creamFaint }}
      >
        <span>Movement</span>
        <span className="text-right">Best</span>
        <span className="text-right">Ref @44</span>
        <span className="text-right">Of ref</span>
      </div>

      {stations.map((s, i) => {
        const low = s.band === 'low'
        const over = s.band === 'over'
        return (
          <motion.div
            key={s.device_name}
            initial={{ opacity: 0, y: 6 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.05 }}
            className="grid items-baseline gap-x-5 py-3.5"
            style={{ gridTemplateColumns: COLS, borderTop: '1px solid ' + TOKENS.creamHairline }}
          >
            <span className="text-[0.9375rem] truncate" style={{ color: low ? TOKENS.terracotta : TOKENS.cream }}>
              {cleanDeviceName(s.device_name)}
            </span>
            <span
              className="text-right tabular-nums text-[0.875rem]"
              style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamSoft }}
            >
              {formatLoad(s.current_max_kg)}
            </span>
            <span
              className="text-right tabular-nums text-[0.875rem]"
              style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamFaint }}
            >
              {formatLoad(s.reference_kg)}
            </span>
            <span
              className="text-right tabular-nums text-[1.0625rem]"
              style={{
                fontFamily: 'var(--font-mono)',
                color: low ? TOKENS.terracotta : over ? TOKENS.amber : TOKENS.cream,
              }}
            >
              {s.pct_of_reference.toFixed(1)}%
            </span>
          </motion.div>
        )
      })}
    </div>
  )
}
